import * as React from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import useNotification from '../notification/notificationContext';
import { NOTIFICATIONS } from '../../config';
import ACTIONS from '../actions';

export const ScansContext = React.createContext();

function useScans() {
  const context = React.useContext(ScansContext);

  if (!context) throw new Error('useScans must be used within a ScansProvider');

  const [scansState, dispatch] = context;

  const { setNotification } = useNotification();

  const getPatientScans = (userId) => {
    return axios.get(`${API_BASE_URL}/patient-scan/${userId}`).then(res => {
      const response = res.data;
      return response;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: err.response?.data?.message || 'We were unable to retrieve scans from the server. Please try again later.'
      })
      throw err;
    })
  }

  const addScan = async (patientId, files, data) => {
    var formData = new FormData();
    Array.from(files).forEach(file=>{
      formData.append("scans", file);
    });
    formData.append("patientId", patientId);
    formData.append("type", data.type);
    formData.append("description", data.description);

    const response = await axios.post(`${API_BASE_URL}/patient-scan/create`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }).then(res => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.SUCCESS,
        title: 'Success',
        message: 'Scan uploaded successfully.'
      })
      return res.data
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: err.response?.data?.error?.message || 'We were unable to retrieve scans from the server. Please try again later.'
      })
      throw err;
    })
    
    return response
  }

  const deleteScan = (scanId) => {
    return axios.delete(`${API_BASE_URL}/patient-scan/delete/${scanId}`).then(res => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.SUCCESS,
        title: 'Success',
        message: 'Scan deleted successfully.'
      });
      return res
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: err.response?.data?.message || 'Unable to delete file'
      })
      throw err;
    })
  };

  const getPatientScansDetails = (id) => {
    return axios.get(`${API_BASE_URL}/patient-scan/get-details/${id}`).then(res => {
      return res.data;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: 'We were unable to get scan details. Please try again later.'
      })
      throw err;
    })
  }
  const getScanFiles = (scanId) => {
    return axios.get(`${API_BASE_URL}/file/patient-scans/${scanId}`).then(res => {
      dispatch({
        type: ACTIONS.GET_SCAN_FILES,
        payload: res.data.data
      });
      return res.data
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: err.response?.data?.message || 'Unable to retrieve scan files'
      });
      throw err;
    })
  };

  const deleteScanFile = (scanId, fileName) => {
    return axios.delete(`${API_BASE_URL}/file/patient-scans/${scanId}/${fileName}`).then(res => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.SUCCESS,
        title: 'Success',
        message: 'File deleted successfully.'
      });
      return res
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: err.response?.data?.message || 'Unable to delete file'
      })
      throw err;
    })
  };

  const downloadScanFile = (scanId, fileName) => {
    axios.get(`${API_BASE_URL}/file/patient-scans/${scanId}/${fileName}`).then(res => {
      const downloadUrl = res.data.url.data;
      const newWindow = window.open(downloadUrl, '_blank', 'noopener,noreferrer');
      if (newWindow) newWindow.opener = null;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: err.response?.data?.message || 'Unable to download file'
      })
      throw err;
    })
  };

  const uploadScanFile = (scanId, files) => {
    var formData = new FormData();
    Array.from(files).forEach(file=>{
      formData.append("scans", file);
    });

    formData.append("scanId", scanId);
    return axios.post(`${API_BASE_URL}/patient-scan/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
    }).then(res => {
      dispatch({
        type: ACTIONS.ADD_SCAN_FILE,
        payload: res.data
      });
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.SUCCESS,
        title: 'Upload success',
        message: 'File was successfully uploaded'
      })
      return res
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: err.response?.data?.message || 'Unable to upload file'
      })
      throw err;
    })
  };

  const getScanFile = (scanId, fileName) => {
    return axios.get(`${API_BASE_URL}/file/patient-scans/${scanId}/${fileName}`).then(res => {
      const downloadUrl = res.data?.url.data;
      return { downloadUrl, patientName: res.data.scanDetails?.user_id?.first_name + ' ' + res.data.scanDetails?.user_id?.last_name }
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: err.response?.data?.message || 'Unable to download file'
      })
      throw err;
    })
  };

  const viewDicomStudy = (scanId) => {
    return axios.get(`${API_BASE_URL}/patient-scan/view-dicom-study/${scanId}`).then(res => {
      if (res.data?.redirectUrl) window.open(res.data?.redirectUrl);
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: err.response?.data?.message || 'Unable to view study'
      })
      throw err;
    })
  };

  const getDicomStudyData = (scanId) => {
    return axios.get(`${API_BASE_URL}/patient-scan/get-dicom-study-data/${scanId}`).then(res => {
      return res.data
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: err.response?.data?.message || 'Unable to fetch study data'
      })
      throw err;
    })
  }

  const downloadDicomStudy = scanId => {
    return axios.get(`${API_BASE_URL}/patient-scan/download-dicom-study/${scanId}`, {
    responseType: 'blob'               // or 'arraybuffer'
  }).then(res => {
      return res
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Server error',
        message: err.response?.data?.message || 'Unable to download study'
      })
      throw err;
    })
  }

  
  
  return {
    scansState,
    dispatch,
    addScan,
    deleteScan,
    getPatientScans,
    getPatientScansDetails,
    getScanFiles,
    deleteScanFile,
    downloadScanFile,
    uploadScanFile,
    getScanFile,
    viewDicomStudy,
    getDicomStudyData,
    downloadDicomStudy
  }
}

export default useScans;